/**
 * The status of an invitation as the screens show it. The server's states are PENDING,
 * ACCEPTED, DECLINED, REVOKED and EXPIRED; a lapsed invitation can still be stored as PENDING
 * until somebody tries to use it, so expiry is also read from the date.
 */

export interface InvitationStatusFields {
  status: string;
  expires_at: string;
}

export const STATUS_COLORS: Record<string, string> = {
  PENDING: 'gold',
  ACCEPTED: 'green',
  DECLINED: 'default',
  REVOKED: 'default',
  EXPIRED: 'default'
};

/** The server stores a lapsed invitation as PENDING, so expiry is read from the date. */
export const isExpired = (invitation: InvitationStatusFields): boolean =>
  invitation.status === 'EXPIRED'
  || (invitation.status === 'PENDING' && new Date(invitation.expires_at).getTime() < Date.now());

/** The status word on the chip: a lapsed PENDING reads EXPIRED. */
export const displayStatus = (invitation: InvitationStatusFields): string =>
  invitation.status === 'PENDING' && isExpired(invitation) ? 'EXPIRED' : invitation.status;

/** The filter chips of an Access page's Invitations tab. Dead = revoked or expired. */
export type InvitationFilter = 'pending' | 'accepted' | 'declined' | 'dead';

export const INVITATION_FILTERS: { value: InvitationFilter; label: string }[] = [
  {
    value: 'pending',
    label: 'Pending'
  }, {
    value: 'accepted',
    label: 'Accepted'
  }, {
    value: 'declined',
    label: 'Declined'
  }, {
    value: 'dead',
    label: 'Dead'
  }
];

/** Which filter chip an invitation falls under. An unknown status counts as dead. */
export const invitationFilterOf = (invitation: InvitationStatusFields): InvitationFilter => {
  if (invitation.status === 'ACCEPTED') return 'accepted';
  if (invitation.status === 'DECLINED') return 'declined';
  if (invitation.status === 'PENDING' && !isExpired(invitation)) return 'pending';
  return 'dead';
};

/** The rows under one filter chip. */
export const filterInvitations = <T extends InvitationStatusFields>(rows: T[], filter: InvitationFilter): T[] =>
  rows.filter((row) => invitationFilterOf(row) === filter);

/** The number of rows under each filter chip. */
export const countInvitations = (rows: InvitationStatusFields[]): Record<InvitationFilter, number> => {
  const counts: Record<InvitationFilter, number> = { 
    pending: 0, 
    accepted: 0, 
    declined: 0, 
    dead: 0 
  };
  rows.forEach((row) => {
    counts[invitationFilterOf(row)] += 1;
  });
  return counts;
};

/**
 * Whether the caller may resend or revoke this row. On an entity's list the server marks the
 * rows the caller may manage; on the organisation's and the platform's every row is manageable.
 */
export const canManageInvitation = (invitation: { manageable?: boolean }, level: string): boolean =>
  level !== 'entity' || invitation.manageable === true;
